import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaGoogleDrive, FaExternalLinkAlt, FaPlus, FaTrash, FaEdit,
  FaFolder, FaFileAlt, FaSyncAlt, FaCopy, FaEye, FaLock, FaGlobe
} from 'react-icons/fa';

export interface GDriveResource {
  id?: number;
  title: string;
  link: string;
  category: string;
  description?: string;
  created_at?: string;
}

const GoogleDriveAdmin: React.FC = () => {
  const [resources, setResources] = useState<GDriveResource[]>([]);
  const [primaryLink, setPrimaryLink] = useState<string>('');
  const [selectedResource, setSelectedResource] = useState<GDriveResource | null>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [message, setMessage] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');

  // Modal / Form States for CRUD operations
  const [showFormModal, setShowFormModal] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<GDriveResource>({
    title: '',
    link: '',
    category: 'General',
    description: '',
  });

  // Fetch GDrive Resources & Primary Link
  const fetchGDriveData = async () => {
    setLoading(true);
    const defaultFolderUrl = 'https://drive.google.com/drive/folders/1-2ZOS1a1N8B54XueDuOI2fmc9g3XicA7';

    try {
      // 1. Fetch resources list
      const resList = await axios.get('https://rmbbackend.rmbjaipur.com/auth/gdrive/resources').catch(() => ({ data: { data: [] } }));
      let list: GDriveResource[] = resList.data?.data || [];

      // 2. Fetch primary link
      const resPrimary = await axios.get('https://rmbbackend.rmbjaipur.com/auth/gdrive').catch(() => ({ data: { link: '' } }));
      const pLink = resPrimary.data?.link || defaultFolderUrl;
      setPrimaryLink(pLink);

      // If no resources in DB, seed the default user folder
      if (list.length === 0) {
        list = [
          {
            id: 1,
            title: 'Rotary RMB Primary Drive Folder',
            link: defaultFolderUrl,
            category: 'Primary',
            description: 'Official Google Drive repository folder containing chapter documents, meeting flyers, and archives.'
          }
        ];
      }

      setResources(list);
      setSelectedResource(list[0]);
    } catch (err) {
      console.error('Error fetching GDrive data:', err);
      setSelectedResource({ title: 'Rotary RMB Primary Drive Folder', link: defaultFolderUrl, category: 'Primary' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGDriveData();
  }, []);

  // Helper: Convert Google Drive Link to Embedded Viewer URL
  const getEmbedUrl = (rawUrl: string): string => {
    if (!rawUrl) return 'https://drive.google.com/embeddedfolderview?id=1-2ZOS1a1N8B54XueDuOI2fmc9g3XicA7#grid';
    try {
      // Folder ID extraction (/folders/ID or /folders/u/0/ID)
      const folderMatch = rawUrl.match(/\/folders\/([a-zA-Z0-9_-]+)/);
      if (folderMatch && folderMatch[1]) {
        return `https://drive.google.com/embeddedfolderview?id=${folderMatch[1]}#grid`;
      }

      // File / Document ID extraction (/file/d/ID/preview or /d/ID)
      const fileMatch = rawUrl.match(/\/d\/([a-zA-Z0-9_-]+)/);
      if (fileMatch && fileMatch[1]) {
        return `https://drive.google.com/file/d/${fileMatch[1]}/preview`;
      }

      // Open ID extraction ([?&]id=ID)
      const idMatch = rawUrl.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      if (idMatch && idMatch[1]) {
        return `https://drive.google.com/embeddedfolderview?id=${idMatch[1]}#grid`;
      }

      return rawUrl;
    } catch (e) {
      return rawUrl;
    }
  };

  // Open Form Modal for Create / Edit
  const handleOpenCreateForm = () => {
    setEditingId(null);
    setFormData({ title: '', link: '', category: 'General', description: '' });
    setShowFormModal(true);
  };

  const handleOpenEditForm = (item: GDriveResource) => {
    setEditingId(item.id || null);
    setFormData({
      title: item.title,
      link: item.link,
      category: item.category || 'General',
      description: item.description || '',
    });
    setShowFormModal(true);
  };

  // Save (Create or Update) Resource
  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.link) return;

    try {
      if (editingId) {
        await axios.put(`https://rmbbackend.rmbjaipur.com/auth/gdrive/resources/${editingId}`, formData);
        setMessage('Drive resource updated successfully!');
      } else {
        await axios.post('https://rmbbackend.rmbjaipur.com/auth/gdrive/resources', formData);
        setMessage('New Drive resource created successfully!');
      }

      setShowFormModal(false);
      fetchGDriveData();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error(err);
      setErrorMsg('Failed to save Drive resource.');
      setTimeout(() => setErrorMsg(''), 3000);
    }
  };

  // Delete Resource
  const handleDeleteResource = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this Google Drive resource?')) return;
    try {
      await axios.delete(`https://rmbbackend.rmbjaipur.com/auth/gdrive/resources/${id}`);
      setMessage('Drive resource deleted.');
      fetchGDriveData();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error(err);
      setErrorMsg('Failed to delete resource.');
      setTimeout(() => setErrorMsg(''), 3000);
    }
  };

  // Copy Link to Clipboard
  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    setMessage('Link copied to clipboard!');
    setTimeout(() => setMessage(''), 2500);
  };

  const embedUrl = selectedResource ? getEmbedUrl(selectedResource.link) : getEmbedUrl(primaryLink);

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 sm:p-6">
      {/* HEADER SECTION */}
      <div className="bg-gradient-to-r from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white p-6 rounded-3xl shadow-xl border border-slate-700/60 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <span className="bg-blue-600 text-white text-[10px] font-black uppercase px-3 py-1 rounded-full tracking-wider shadow flex items-center gap-1.5 w-fit mb-2">
            <FaGoogleDrive /> Cloud Document & Storage Workbench
          </span>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight flex items-center gap-2">
            📂 Google Drive Interactive Studio
          </h1>
          <p className="text-xs text-slate-300 font-semibold mt-1">
            Access, view, manage, and perform full CRUD operations on all saved Google Drive folders, documents, and event media!
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={fetchGDriveData}
            className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold rounded-2xl text-xs shadow transition flex items-center gap-2 cursor-pointer border border-slate-600"
          >
            <FaSyncAlt className={loading ? 'animate-spin' : ''} /> Refresh Data
          </button>
          <button
            type="button"
            onClick={handleOpenCreateForm}
            className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-black rounded-2xl text-xs shadow-lg transition flex items-center gap-2 cursor-pointer"
          >
            <FaPlus /> Add Drive Folder / File
          </button>
        </div>
      </div>

      {/* NOTIFICATION MESSAGES */}
      {message && (
        <div className="bg-emerald-50 border border-emerald-300 text-emerald-900 px-4 py-3 rounded-2xl text-xs font-bold shadow-sm animate-fade-in flex items-center justify-between">
          <span>✅ {message}</span>
        </div>
      )}
      {errorMsg && (
        <div className="bg-red-50 border border-red-300 text-red-900 px-4 py-3 rounded-2xl text-xs font-bold shadow-sm animate-fade-in">
          <span>⚠️ {errorMsg}</span>
        </div>
      )}

      {/* MAIN WORKBENCH GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* LEFT COLUMN: SAVED DRIVE RESOURCES LIST (CRUD MANAGER) */}
        <div className="lg:col-span-4 space-y-4">
          <div className="bg-white p-5 rounded-3xl shadow-sm border border-slate-200 space-y-4">
            <div className="flex items-center justify-between border-b pb-3">
              <h2 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                <FaFolder className="text-amber-500" /> Saved Drive Resources ({resources.length})
              </h2>
            </div>

            {resources.length === 0 ? (
              <div className="p-6 bg-slate-50 rounded-2xl border border-dashed border-slate-300 text-center text-xs text-slate-400 font-bold space-y-2">
                <FaGoogleDrive className="text-4xl text-slate-300 mx-auto" />
                <p>No Google Drive folders saved yet.</p>
                <button
                  type="button"
                  onClick={handleOpenCreateForm}
                  className="px-3 py-1.5 bg-blue-600 text-white font-black rounded-xl text-[11px] shadow inline-flex items-center gap-1 cursor-pointer"
                >
                  <FaPlus /> Add First Folder
                </button>
              </div>
            ) : (
              <div className="space-y-2.5 max-h-[550px] overflow-y-auto pr-1">
                {resources.map(item => (
                  <div
                    key={item.id}
                    onClick={() => setSelectedResource(item)}
                    className={`p-3.5 rounded-2xl border-2 cursor-pointer transition-all flex flex-col gap-2 ${selectedResource?.id === item.id || selectedResource?.link === item.link
                        ? 'border-blue-600 bg-blue-50/70 shadow-sm'
                        : 'border-slate-200 bg-slate-50/50 hover:border-slate-300'
                      }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <span className="p-2 rounded-xl bg-amber-100 text-amber-700 text-sm flex-shrink-0">
                          {item.category === 'Photos' ? '🖼️' : item.category === 'Documents' ? '📜' : '📁'}
                        </span>
                        <div className="truncate">
                          <h3 className="text-xs font-black text-slate-900 truncate">{item.title}</h3>
                          <span className="bg-slate-200 text-slate-700 text-[9px] font-bold px-2 py-0.5 rounded-md uppercase">
                            {item.category || 'General'}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditForm(item);
                          }}
                          className="p-1.5 text-slate-500 hover:text-blue-600 rounded-lg hover:bg-white transition"
                          title="Edit Resource"
                        >
                          <FaEdit size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (item.id) handleDeleteResource(item.id);
                          }}
                          className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-white transition"
                          title="Delete Resource"
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    </div>

                    {item.description && (
                      <p className="text-[10px] text-slate-500 font-medium line-clamp-2 pl-1">
                        {item.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between text-[10px] font-bold text-slate-400 pt-1 border-t border-slate-100">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyLink(item.link);
                        }}
                        className="hover:text-blue-600 flex items-center gap-1"
                      >
                        <FaCopy /> Copy Link
                      </button>
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-blue-600 hover:underline flex items-center gap-1"
                      >
                        Open <FaExternalLinkAlt size={9} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: LIVE INTERACTIVE GOOGLE DRIVE EXPLORER IFRAME VIEWER */}
        <div className="lg:col-span-8 space-y-4">
          <div className="bg-white p-5 rounded-3xl shadow-sm border border-slate-200 space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b pb-3">
              <div>
                <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Interactive File Viewer</span>
                <h2 className="text-sm font-black text-slate-900 flex items-center gap-2">
                  <FaEye className="text-blue-600" /> {selectedResource?.title || 'Google Drive Folder'}
                </h2>
              </div>

              {selectedResource?.link && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleCopyLink(selectedResource.link)}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition flex items-center gap-1 cursor-pointer"
                  >
                    <FaCopy /> Copy Link
                  </button>

                  <a
                    href={selectedResource.link}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs transition flex items-center gap-1 cursor-pointer shadow"
                  >
                    <FaExternalLinkAlt /> Open in Google Drive
                  </a>
                </div>
              )}
            </div>

            {/* GOOGLE DRIVE EMBED NOTICE & DIRECT LINK BAR */}
            <div className="bg-amber-50 dark:bg-slate-800 p-3 rounded-2xl border border-amber-300 dark:border-amber-500/40 text-xs text-amber-900 dark:text-amber-200 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-xs">
              <div className="flex items-center gap-2">
                <span className="text-lg flex-shrink-0">🌐</span>
                <div>
                  <div className="font-black text-slate-900 dark:text-amber-300 text-xs">Direct Link: {selectedResource?.link || primaryLink}</div>
                  <div className="text-[11px] text-slate-600 dark:text-slate-300 font-medium">
                    If browser security blocks Google iframe preview ("refused to connect"), click <strong>Open Folder Directly</strong> to access all documents & folders on Google Drive.
                  </div>
                </div>
              </div>
              <a
                href={selectedResource?.link || primaryLink}
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-black rounded-xl text-xs flex items-center gap-2 cursor-pointer shadow-md flex-shrink-0 transition"
              >
                <FaExternalLinkAlt /> Open Folder Directly
              </a>
            </div>

            {/* LIVE IFRAME CONTAINER */}
            {selectedResource?.link ? (
              <div className="w-full h-[620px] bg-slate-900 rounded-2xl overflow-hidden shadow-inner border border-slate-200 relative group">
                <iframe
                  src={embedUrl}
                  title={selectedResource.title}
                  className="w-full h-full border-0 bg-white"
                  allow="autoplay"
                />
              </div>
            ) : (
              <div className="w-full h-[500px] bg-slate-50 rounded-2xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center p-6 text-center text-slate-400 space-y-3">
                <FaGoogleDrive className="text-6xl text-blue-500 animate-pulse" />
                <h3 className="font-black text-slate-800 text-base">No Google Drive Folder Selected</h3>
                <p className="text-xs text-slate-500 max-w-sm">
                  Save a shareable Google Drive folder link on the left to immediately access, view, and manage all files & folders live!
                </p>
                <a
                  href="https://drive.google.com/drive/folders/1-2ZOS1a1N8B54XueDuOI2fmc9g3XicA7"
                  target="_blank"
                  rel="noreferrer"
                  className="px-4 py-2 bg-blue-600 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 shadow"
                >
                  <FaExternalLinkAlt /> Open Primary Drive Folder
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CREATE / EDIT RESOURCE MODAL */}
      {showFormModal && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-lg p-6 rounded-3xl shadow-2xl space-y-4 border border-slate-200 animate-scale-in">
            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="text-base font-black text-slate-900 flex items-center gap-2">
                <FaGoogleDrive className="text-blue-600" />
                {editingId ? 'Edit Drive Resource' : 'Add New Google Drive Folder / Resource'}
              </h3>
              <button
                type="button"
                onClick={() => setShowFormModal(false)}
                className="text-slate-400 hover:text-slate-700 font-black text-lg p-1"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitForm} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Resource Title *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g. Official Chapter Meeting Minutes & Photos"
                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-bold text-slate-800 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Google Drive Link (URL) *</label>
                <input
                  type="url"
                  required
                  value={formData.link}
                  onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                  placeholder="https://drive.google.com/drive/folders/..."
                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-bold text-slate-800 focus:ring-2 focus:ring-blue-500 outline-none"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  Ensure the folder sharing permission is set to "Anyone with the link can view".
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Resource Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-bold text-slate-800 focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                >
                  <option value="General">General Storage</option>
                  <option value="Photos">Event & Member Photos</option>
                  <option value="Documents">Meeting Minutes & Docs</option>
                  <option value="Roster">Member Directory & Roster</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Description (Optional)</label>
                <textarea
                  rows={2}
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Additional notes about this drive resource..."
                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-bold text-slate-800 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t">
                <button
                  type="button"
                  onClick={() => setShowFormModal(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-xl text-xs shadow transition flex items-center gap-1.5 cursor-pointer"
                >
                  {editingId ? 'Update Resource' : 'Save Drive Resource'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleDriveAdmin;
